import React, { useCallback, useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import styled from 'styled-components';

import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { preservePeriodParam } from 'modules/insights-v2/hooks/useInsightsSubRoutes';

import { FtuByVenueTableColumnIDs, FtuByVenueTableSortableColumnIDs, FtuByVenueTotalItem } from './types';
import InfiniteTable from '../tables/InfiniteTable';
import { SortOrder, SortState } from '../tables/types';

type FtuByVenueTableProps = {
  className?: string;
  fetchingNextPage: boolean;
  fetchingPreviousPage: boolean;
  onFetchNextPage?: () => void;
  onFetchPreviousPage?: () => void;
  items: FtuByVenueTotalItem[];
  handleSort: (sort: SortState<FtuByVenueTableSortableColumnIDs>) => void;
  sort?: SortState<FtuByVenueTableSortableColumnIDs>;
  visibleRowsCount: number | undefined;
};

const TrimmedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const getColumnsDefinition = (
  intl: IntlShape,
  handleSort: (sort: SortState<FtuByVenueTableSortableColumnIDs>) => void,
) => [
  {
    id: FtuByVenueTableColumnIDs.VENUE,
    label: intl.formatMessage({ id: 'insights.customer-base.table.venue-name' }),
    render: (record: FtuByVenueTotalItem) => <TrimmedText title={record.venueName}>{record.venueName}</TrimmedText>,
    width: 16,
  },
  {
    id: 'total-customers',
    label: intl.formatMessage({ id: 'insights.customer-base.table.total-customers' }),
    columns: [
      {
        id: FtuByVenueTableColumnIDs.FIRST_TIME_CUSTOMERS_COUNT,
        label: intl.formatMessage({ id: 'insights.customer-base.table.new-customers' }),
        shortLabel: intl.formatMessage({ id: 'insights.customer-base.table.new-customers' }),
        render: (record: FtuByVenueTotalItem) => record.firstTimeCustomersCount,
        enableSort: true,
        handleSort: (sortOrder: SortOrder) =>
          handleSort({ sortBy: FtuByVenueTableColumnIDs.FIRST_TIME_CUSTOMERS_COUNT, sortOrder }),
        width: 14,
      },
      {
        id: FtuByVenueTableColumnIDs.RETURNING_CUSTOMERS_COUNT,
        label: intl.formatMessage({ id: 'insights.customer-base.table.returning-customers' }),
        shortLabel: intl.formatMessage({ id: 'insights.customer-base.table.returning-customers' }),
        render: (record: FtuByVenueTotalItem) => record.returningCustomersCount,
        enableSort: true,
        handleSort: (sortOrder: SortOrder) =>
          handleSort({ sortBy: FtuByVenueTableColumnIDs.RETURNING_CUSTOMERS_COUNT, sortOrder }),
        width: 14,
      },
    ],
  },
  {
    id: 'avg-basket-items',
    label: intl.formatMessage({ id: 'insights.customer-base.table.avg-basket-items' }),
    columns: [
      {
        id: FtuByVenueTableColumnIDs.FIRST_TIME_AVG_BASKET_ITEMS,
        label: intl.formatMessage({ id: 'insights.customer-base.table.new-customers-avg-basket-items' }),
        shortLabel: intl.formatMessage({ id: 'insights.customer-base.table.new-customers' }),
        render: (record: FtuByVenueTotalItem) => record.firstTimeAvgBasketSizeItems,
        enableSort: true,
        handleSort: (sortOrder: SortOrder) =>
          handleSort({ sortBy: FtuByVenueTableColumnIDs.FIRST_TIME_AVG_BASKET_ITEMS, sortOrder }),
        width: 14,
      },
      {
        id: FtuByVenueTableColumnIDs.RETURNING_AVG_BASKET_ITEMS,
        label: intl.formatMessage({ id: 'insights.customer-base.table.returning-customers-avg-basket-items' }),
        shortLabel: intl.formatMessage({ id: 'insights.customer-base.table.returning-customers' }),
        render: (record: FtuByVenueTotalItem) => record.returningAvgBasketSizeItems,
        enableSort: true,
        handleSort: (sortOrder: SortOrder) =>
          handleSort({ sortBy: FtuByVenueTableColumnIDs.RETURNING_AVG_BASKET_ITEMS, sortOrder }),
        width: 14,
      },
    ],
  },
  {
    id: 'avg-basket-size',
    label: intl.formatMessage({ id: 'insights.customer-base.table.avg-basket-size' }),
    columns: [
      {
        id: FtuByVenueTableColumnIDs.FIRST_TIME_AVG_BASKET_SIZE,
        label: intl.formatMessage({ id: 'insights.customer-base.table.new-customers-avg-basket-size' }),
        shortLabel: intl.formatMessage({ id: 'insights.customer-base.table.new-customers' }),
        render: (record: FtuByVenueTotalItem) => (
          <MonetaryAmount amount={record.firstTimeAvgBasketSize} currency={record.currency} />
        ),
        enableSort: true,
        handleSort: (sortOrder: SortOrder) =>
          handleSort({ sortBy: FtuByVenueTableColumnIDs.FIRST_TIME_AVG_BASKET_SIZE, sortOrder }),
        width: 14,
      },
      {
        id: FtuByVenueTableColumnIDs.RETURNING_AVG_BASKET_SIZE,
        label: intl.formatMessage({ id: 'insights.customer-base.table.returning-customers-avg-basket-size' }),
        shortLabel: intl.formatMessage({ id: 'insights.customer-base.table.returning-customers' }),
        render: (record: FtuByVenueTotalItem) => (
          <MonetaryAmount amount={record.returningAvgBasketSize} currency={record.currency} />
        ),
        enableSort: true,
        handleSort: (sortOrder: SortOrder) =>
          handleSort({ sortBy: FtuByVenueTableColumnIDs.RETURNING_AVG_BASKET_SIZE, sortOrder }),
        width: 14,
      },
    ],
  },
];

const FtuByVenueTable = ({
  className,
  items,
  fetchingNextPage,
  fetchingPreviousPage,
  sort,
  onFetchNextPage,
  onFetchPreviousPage,
  handleSort,
  visibleRowsCount,
}: FtuByVenueTableProps) => {
  const intl = useIntl();
  const link = useCallback((record: FtuByVenueTotalItem) => preservePeriodParam(`/venue/${record.id}/insights`), []);
  const columns = useMemo(() => getColumnsDefinition(intl, handleSort), [intl, handleSort]);

  return (
    <InfiniteTable
      className={className}
      items={items}
      fetchingNextPage={fetchingNextPage}
      fetchingPreviousPage={fetchingPreviousPage}
      onFetchNextPage={onFetchNextPage}
      onFetchPreviousPage={onFetchPreviousPage}
      link={link}
      sort={sort}
      visibleRowsCount={visibleRowsCount}
      columns={columns}
    />
  );
};

export default FtuByVenueTable;
